import {
  createOrUpdateRangePayoutBracketRest,
  createPayoutStructureRest,
  deletePayoutStructureRest,
  duplicatePayoutStructureRest,
  getBracketsByStructureIdRest,
  getPayoutStructuresRest,
  updatePayoutStructureRest,
  validateBracketsRest,
} from '@/services/tournaments/tournaments-service';
import { CreatePayoutBracketDto, PayoutStructureType } from '@repo/common-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';

const usePayoutStructures = () => {
  const queryClient = useQueryClient();

  const {
    data: payoutStructures,
    isLoading,
    refetch: refetchPayoutStructures,
  } = useQuery({
    queryKey: ['payoutStructures'],
    queryFn: () => getPayoutStructuresRest(),
    staleTime: 60000,
  });

  const { mutateAsync: createOrUpdateRangeBracketsMutation } = useMutation({
    mutationFn: (data: { payoutStructureId: string; brackets: CreatePayoutBracketDto[] }) =>
      createOrUpdateRangePayoutBracketRest(data.payoutStructureId, data.brackets),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['payoutStructures'] });
      queryClient.invalidateQueries({ queryKey: ['payoutBrackets', variables.payoutStructureId] });
    },
  });

  const { mutateAsync: createPayoutStructureMutation } = useMutation({
    mutationFn: async (data: { name: string }) => {
      return await createPayoutStructureRest({
        name: data.name,
        type: PayoutStructureType.STANDARD,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payoutStructures'] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to create payout structure');
    },
  });

  const { mutateAsync: validateBracketsMutation } = useMutation({
    mutationFn: (data: CreatePayoutBracketDto[]) => validateBracketsRest(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payoutStructures'] });
    },
  });

  const { mutateAsync: updatePayoutStructureMutation } = useMutation({
    mutationFn: (data: { id: string; name: string }) => updatePayoutStructureRest(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['payoutStructures'] });
      queryClient.invalidateQueries({ queryKey: ['payoutBrackets', variables.id] });
    },
  });

  const { mutateAsync: deletePayoutStructureMutation } = useMutation({
    mutationFn: (id: string | undefined) => deletePayoutStructureRest(id ?? ''),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['payoutStructures'] });
      queryClient.invalidateQueries({ queryKey: ['payoutBrackets', id] });
      refetchPayoutStructures();
    },
  });

  const { mutateAsync: duplicatePayoutStructureMutation } = useMutation({
    mutationFn: (id: string) => duplicatePayoutStructureRest(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payoutStructures'] });
      queryClient.invalidateQueries({ queryKey: ['payoutBrackets'] });
      refetchPayoutStructures();
    },
  });

  return {
    payoutStructures,
    createPayoutStructureMutation,
    isLoading,
    createOrUpdateRangeBracketsMutation,
    validateBracketsMutation,
    updatePayoutStructureMutation,
    deletePayoutStructureMutation,
    duplicatePayoutStructureMutation,
  };
};

export const usePayoutBrackets = (structureId: string) =>
  useQuery({
    queryKey: ['payoutBrackets', structureId],
    queryFn: () => getBracketsByStructureIdRest(structureId),
    enabled: !!structureId,
    staleTime: 60000,
  });

export default usePayoutStructures;
